/**
 * 字幕处理工具函数
 */

/**
 * 将秒数转换为 SRT 时间格式 HH:MM:SS,mmm
 */
export function formatSrtTime(seconds) {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`
}

/**
 * 将 Pollinations verbose_json 转录结果转换为 SRT 格式
 */
export function transcriptionToSrt(segments) {
  if (!segments || !Array.isArray(segments)) return ''

  return segments
    .map((seg, index) => {
      const start = formatSrtTime(seg.start)
      const end = formatSrtTime(seg.end)
      return `${index + 1}\n${start} --> ${end}\n${seg.text.trim()}\n`
    })
    .join('\n')
}

/**
 * 将 Pollinations verbose_json 转录结果转换为 VTT 格式
 */
export function transcriptionToVtt(segments) {
  if (!segments || !Array.isArray(segments)) return ''

  const cues = segments
    .map((seg) => {
      const start = formatVttTime(seg.start)
      const end = formatVttTime(seg.end)
      return `${start} --> ${end}\n${seg.text.trim()}`
    })
    .join('\n\n')

  return `WEBVTT\n\n${cues}`
}

function formatVttTime(seconds) {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(3, '0')}`
}

/**
 * 解析 SRT 字幕为结构化数据
 */
export function parseSrt(srtContent) {
  const blocks = srtContent.trim().split(/\n\s*\n/)
  return blocks
    .map((block) => {
      const lines = block.trim().split('\n')
      if (lines.length < 3) return null

      const timeLine = lines[1]
      const timeMatch = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/)
      if (!timeMatch) return null

      const text = lines.slice(2).join('\n')
      return {
        id: parseInt(lines[0]),
        start: parseSrtTime(timeMatch[1]),
        end: parseSrtTime(timeMatch[2]),
        text: text.trim(),
      }
    })
    .filter(Boolean)
}

function parseSrtTime(timeStr) {
  const [h, m, sMs] = timeStr.split(':')
  const [s, ms] = sMs.split(',')
  return parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s) + parseInt(ms) / 1000
}

/**
 * 下载文本内容为文件
 */
export function downloadTextFile(content, filename, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * 支持的语言列表
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'zh', name: '中文 (Chinese)' },
  { code: 'en', name: 'English' },
  { code: 'ja', name: '日本語 (Japanese)' },
  { code: 'ko', name: '한국어 (Korean)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'ru', name: 'Русский (Russian)' },
  { code: 'ar', name: 'العربية (Arabic)' },
  { code: 'pt', name: 'Português (Portuguese)' },
  { code: 'it', name: 'Italiano (Italian)' },
  { code: 'hi', name: 'हिन्दी (Hindi)' },
  { code: 'th', name: 'ไทย (Thai)' },
  { code: 'vi', name: 'Tiếng Việt (Vietnamese)' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'tr', name: 'Türkçe (Turkish)' },
]

/**
 * TTS 可用音色列表
 */
export const TTS_VOICES = [
  { id: 'alloy', name: 'Alloy', description: '中性、平衡' },
  { id: 'echo', name: 'Echo', description: '男性、低沉' },
  { id: 'fable', name: 'Fable', description: '男性、英国口音' },
  { id: 'onyx', name: 'Onyx', description: '男性、深沉' },
  { id: 'nova', name: 'Nova', description: '女性、温暖' },
  { id: 'shimmer', name: 'Shimmer', description: '女性、明亮' },
]